import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	AfasResourceKind,
	AfasResultStatus,
	MySignalsListModel,
	SignalListItem,
	getMySignals
} from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";
import { AfasLiveInformationProvider } from "client/Features/Workspace/Tiles/Afas/provider";
import { Guid } from "client_legacy/ndaw/guid";
import { LiveTileContent } from "../LiveTileContent";
import { localize } from "client_legacy/ndaw/culture";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import {
	BusyIndicator,
	Button,
	ChangeTriggerKind,
	DateString
} from "client/Components";
import { useAfasLiveInformation } from "./hooks";
import { ListItem } from "../ListItem";
import { isNil, isNotNil } from "client/Misc/utils";
import { OtpDialog } from "./OtpDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AlertManager, useAlerts } from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import styles from "./MySignals.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

const PAGE_SIZE: number = 10;

export type MySignalsProps = {
	provider: AfasLiveInformationProvider;
	tabId: Guid;
	connectionId: number;
	onNavigate: () => void;
	timezone: TimeZone;
};

export const MySignals = (props: MySignalsProps): ReactElement => {
	const {
		provider,
		tabId,
		connectionId,
		onNavigate,
		timezone
	} = props;

	const [didUserManuallyLoadMore, setDidUserManuallyLoadMore] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isOtpDialogOpen, setIsOtpDialogOpen] = useState<boolean>(false);

	const loadMoreAndSearchAbortControllerRef: RefObject<AbortController | null>
		= useRef<AbortController>(null);

	const alertManager: AlertManager = useAlerts();

	const {
		loadingState,
		setLoadingState,
		reload: refreshHandler
	} = useAfasLiveInformation(
		provider,
		tabId,
		AfasResourceKind.MySignals,
		connectionId,
		didUserManuallyLoadMore || Boolean(searchTerm)
	);

	const loadMoreHandler = useCallback(
		async (): Promise<void> => {
			if (loadingState.isLoading === true
				|| isNil(loadingState.response)
				|| !loadingState.response.hasMore
			) {
				return;
			}

			setDidUserManuallyLoadMore(true);

			const currentResponse: LazyLoadPageResult<SignalListItem> = loadingState.response;

			setLoadingState({
				isLoading: true,
				response: currentResponse
			});

			const skip: number = currentResponse.items
				.length;

			try {
				loadMoreAndSearchAbortControllerRef.current
					?.abort();

				const cancellation = new AbortController();
				loadMoreAndSearchAbortControllerRef.current = cancellation;

				const results: MySignalsListModel = await getMySignals(
					connectionId,
					skip,
					PAGE_SIZE,
					searchTerm,
					loadMoreAndSearchAbortControllerRef.current
						.signal
				);

				switch (results.status) {
					case AfasResultStatus.Success:
						setLoadingState({
							isLoading: false,
							response: {
								hasMore: results.items.hasMore,
								items: [
									...currentResponse.items,
									...results.items.items
								]
							}
						});
						return;
					case AfasResultStatus.GeneralFailure:
						setLoadingState({
							isLoading: false,
							response: {
								...currentResponse,
								hasMore: false
							}
						});
						break;
					case AfasResultStatus.RequireNewToken:
					case AfasResultStatus.NoAccess:
						setLoadingState({
							isLoading: false,
							error: results.status
						});
						break;
					default:
						never(results);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				setLoadingState({
					isLoading: false,
					response: {
						...currentResponse,
						hasMore: false
					}
				});

				alertManager.showError(localize("_GeneralErrorMessage"));
			}

		},
		[
			alertManager,
			connectionId,
			loadingState,
			searchTerm,
			setLoadingState
		]
	);

	const searchHandler = useCallback(
		async (newSearchValue: string): Promise<void> => {
			if (!loadingState.isLoading && isNotNil(loadingState.error)) {
				return;
			}

			loadMoreAndSearchAbortControllerRef.current
				?.abort();

			setDidUserManuallyLoadMore(false);
			setSearchTerm(newSearchValue);

			setLoadingState({
				isLoading: true
			});

			try {
				const cancellation = new AbortController();
				loadMoreAndSearchAbortControllerRef.current = cancellation;

				const results: MySignalsListModel = await getMySignals(
					connectionId,
					0,
					PAGE_SIZE,
					newSearchValue,
					loadMoreAndSearchAbortControllerRef.current
						.signal
				);

				switch (results.status) {
					case AfasResultStatus.Success:
						setLoadingState({
							isLoading: false,
							response: results.items
						});
						return;
					case AfasResultStatus.GeneralFailure:
						console.error("Unable to retrieve items");

						setLoadingState({
							isLoading: false,
							hasGlobalFailure: true
						});
						break;
					case AfasResultStatus.RequireNewToken:
					case AfasResultStatus.NoAccess:
						setLoadingState({
							isLoading: false,
							error: results.status
						});
						break;
					default:
						never(results);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				setLoadingState({
					isLoading: false,
					hasGlobalFailure: true
				});

				alertManager.showError(localize("_GeneralErrorMessage"));
			}

		},
		[
			alertManager,
			connectionId,
			loadingState,
			setLoadingState
		]
	);

	const scrollHandler = useCallback(
		(scrollTop: number): void => {
			if (!didUserManuallyLoadMore || searchTerm) {
				return;
			}

			if (scrollTop <= 0) {
				loadMoreAndSearchAbortControllerRef.current
					?.abort();

				setDidUserManuallyLoadMore(false);
			}
		},
		[
			didUserManuallyLoadMore,
			searchTerm
		]
	);

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading === false) {
			if (loadingState.hasTooManyConsecutiveErrors) {
				return <TileRefresh onRefresh={refreshHandler} />;
			}

			if (loadingState.hasGlobalFailure) {
				return (
					<LiveTileContent.Message
						text={localize("_UnableToRetrieveItems")}
					/>
				);
			}

			if (isNotNil(loadingState.error)) {
				return (
					<>
						<LiveTileContent.Message
							text={localize(
								loadingState.error === AfasResultStatus.RequireNewToken
									? "_AfasActivationIsRequiredMessage"
									: "_AfasNoAppAccessAndSuggestToActivateMessage"
							)}
						/>
						<div className={styles.activateButtonContainer}>
							<Button
								onClick={() => setIsOtpDialogOpen(true)}
								text={localize("_Activate")}
								kind="primary"
								color="app"
							/>
						</div>
					</>
				);
			}
		}
		if (
			loadingState.response
				?.items
				.length === 0
		) {
			return (
				<LiveTileContent.Message
					text={localize(
						searchTerm === ""
							? "_YouDontHaveAnySignals"
							: "_NoResultsFound"
					)}
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable
				onScroll={scrollHandler}
			>
				<LiveTileContent.List>
					{loadingState.response?.items
						.map((x) => (
							<MySignalsListItem
								key={x.id}
								item={x}
								onNavigate={onNavigate}
								timezone={timezone}
							/>
						))
					}
				</LiveTileContent.List>
				{loadingState.response?.hasMore && (
					<LiveTileContent.LoadMoreButton onClick={loadMoreHandler}/>
				)}
			</LiveTileContent.Scrollable>
		);
	};

	useEffect(
		(): EffectWithDestructorResult => {
			return () => loadMoreAndSearchAbortControllerRef.current
				?.abort();
		},
		[]
	);

	return (
		<>
			{(loadingState.isLoading || isNil(loadingState.error)) && (
				<LiveTileContent.SearchInput
					onChange={searchHandler}
					trigger={{ kind: ChangeTriggerKind.Debounced }}
					placeholder={localize("_Search")}
				/>
			)}
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}
				{renderContent()}
			</LiveTileContent.BusyIndicatorContainer>
			{isOtpDialogOpen && (
				<OtpDialog
					onClose={() => setIsOtpDialogOpen(false)}
					connectionId={connectionId}
					onAfterSave={refreshHandler}
				/>
			)}
		</>

	);
};
MySignals.displayName="MySignals";

type MySignalsListItemProps = {
	item: SignalListItem;
	onNavigate: () => void;
	timezone: TimeZone;
};

const MySignalsListItem = (props: MySignalsListItemProps): ReactElement => {
	const {
		item,
		onNavigate,
		timezone
	} = props;

	return (
		<ListItem
			onClick={onNavigate}
			isHoverable
		>
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title>
						{item.description}
					</ListItem.Title>
				</ListItem.Row>
				<ListItem.Row>
					<ListItem.Info>
						{item.type}
					</ListItem.Info>
					<ListItem.RightAddition>
						<ListItem.Info>
							<DateString
								value={item.creationDate}
								format="AbsoluteTimestamp"
								timezone={timezone}
							/>
						</ListItem.Info>
					</ListItem.RightAddition>
				</ListItem.Row>
			</ListItem.Content>
		</ListItem>
	);
};

MySignalsListItem.displayName = "MySignals.ListItem";